// src/services/indexedDB/repositories/timeBoxRepository.ts
// TimeBox 專屬邏輯

import { TimeBox } from '../../../types/timeBox';
import { BaseRepository } from '../core/base-repository';
import { STORE_NAMES, initDB } from '../core/database';
import { CachedEntity } from '../core/types';
import { formatDateString } from '../utils/date-helpers';
import {
  isRecentlyDeleted,
  markAsRecentlyDeleted,
  shouldSkipRemote,
} from '../utils/cache-manager';
import { logError, logDev } from '../../../utils/logger';

/**
 * TimeBox with dateString for indexing
 */
interface TimeBoxWithDate extends TimeBox {
  dateString?: string;
}

/**
 * TimeBox Repository
 * 處理 TimeBox 相關的 IndexedDB 操作
 */
class TimeBoxRepository extends BaseRepository<TimeBox> {
  protected readonly storeName = STORE_NAMES.TIME_BOXES;

  /**
   * 準備 TimeBox 以儲存（添加 dateString）
   */
  protected prepareForStorage(entity: TimeBox): CachedEntity<TimeBox> {
    const prepared = super.prepareForStorage(entity) as CachedEntity<TimeBoxWithDate>;

    // 添加 dateString 用於索引查詢
    const startDate =
      entity.startTime instanceof Date ? entity.startTime : new Date(entity.startTime);
    prepared.dateString = formatDateString(startDate);

    // 序列化時間欄位
    if (entity.startTime instanceof Date) {
      prepared.startTime = entity.startTime.getTime() as any;
    }
    if (entity.endTime instanceof Date) {
      prepared.endTime = entity.endTime.getTime() as any;
    }

    return prepared as CachedEntity<TimeBox>;
  }

  /**
   * 反序列化 TimeBox（移除 dateString 並轉換日期）
   */
  protected deserialize(stored: CachedEntity<TimeBox>): TimeBox {
    const { cachedAt, dateString, ...entity } = stored as CachedEntity<TimeBoxWithDate>;

    // 轉換日期欄位
    return {
      ...entity,
      startTime:
        typeof entity.startTime === 'number' ? new Date(entity.startTime) : entity.startTime,
      endTime: typeof entity.endTime === 'number' ? new Date(entity.endTime) : entity.endTime,
      createdAt:
        typeof entity.createdAt === 'number' ? new Date(entity.createdAt) : entity.createdAt,
      updatedAt: entity.updatedAt
        ? typeof entity.updatedAt === 'number'
          ? new Date(entity.updatedAt)
          : entity.updatedAt
        : undefined,
    };
  }

  /**
   * 批量保存 TimeBoxes（帶衝突檢測）
   */
  async saveMany(userId: string, timeBoxes: TimeBox[]): Promise<void> {
    if (!timeBoxes.length) return;

    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      const promises: Promise<void>[] = [];

      for (const box of timeBoxes) {
        // 跳過最近被刪除的 TimeBox
        if (isRecentlyDeleted('TimeBox', box.id)) {
          logDev('Skipping recently deleted TimeBox', { id: box.id });
          continue;
        }

        // 檢查本地是否有更新的版本
        const promise = new Promise<void>((resolve) => {
          const getRequest = store.get(box.id);

          getRequest.onsuccess = () => {
            const existing = getRequest.result;

            // 衝突檢測
            if (shouldSkipRemote(existing, box)) {
              resolve();
              return;
            }

            // 保存雲端數據
            const prepared = this.prepareForStorage({ ...box, userId } as TimeBox);
            const putRequest = store.put(prepared);

            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => resolve(); // 單個失敗不影響整體
          };

          getRequest.onerror = () => {
            // 讀取失敗，保守處理：保存雲端數據
            const prepared = this.prepareForStorage({ ...box, userId } as TimeBox);
            store.put(prepared);
            resolve();
          };
        });

        promises.push(promise);
      }

      // 等待所有操作完成
      await Promise.all(promises);

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      logError('Failed to save TimeBoxes to IndexedDB', error);
      throw error;
    }
  }

  /**
   * 按日期獲取 TimeBoxes
   */
  async getByDate(userId: string, date: Date): Promise<TimeBox[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_date');

      const dateString = formatDateString(date);

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, dateString]);

        request.onsuccess = () => {
          const results = request.result.map((item: CachedEntity<TimeBox>) =>
            this.deserialize(item)
          );
          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to get TimeBoxes by date', error);
      throw error;
    }
  }

  /**
   * 獲取當前時間箱
   */
  async getCurrent(userId: string): Promise<TimeBox | null> {
    try {
      const allTimeBoxes = await this.getAll(userId);
      const now = new Date();

      // 查找當前時間所在的時間箱
      const currentTimeBox =
        allTimeBoxes.find((box) => {
          const startTime = new Date(box.startTime);
          const endTime = new Date(box.endTime);
          return startTime <= now && endTime >= now && box.status !== 'cancelled';
        }) || null;

      return currentTimeBox;
    } catch (error) {
      logError('Failed to get current TimeBox', error);
      throw error;
    }
  }

  /**
   * 獲取下一個時間箱
   */
  async getNext(userId: string): Promise<TimeBox | null> {
    try {
      const allTimeBoxes = await this.getAll(userId);
      const now = new Date();

      // 查找未來最接近當前時間的時間箱
      const upcomingTimeBoxes = allTimeBoxes
        .filter((box) => {
          const startTime = new Date(box.startTime);
          return startTime > now && box.status !== 'cancelled';
        })
        .sort((a, b) => {
          const startTimeA = new Date(a.startTime).getTime();
          const startTimeB = new Date(b.startTime).getTime();
          return startTimeA - startTimeB;
        });

      return upcomingTimeBoxes.length > 0 ? upcomingTimeBoxes[0] : null;
    } catch (error) {
      logError('Failed to get next TimeBox', error);
      throw error;
    }
  }

  /**
   * 更新 TimeBox 狀態
   */
  async updateStatus(id: string, status: string): Promise<void> {
    try {
      await this.updateLocal(id, { status } as Partial<TimeBox>);
    } catch (error) {
      logError('Failed to update TimeBox status', error);
      throw error;
    }
  }

  /**
   * 刪除 TimeBox（帶防護標記）
   */
  async deleteLocal(id: string): Promise<void> {
    markAsRecentlyDeleted('TimeBox', id);
    await super.deleteLocal(id);
  }

  /**
   * 檢查是否最近被刪除
   */
  isRecentlyDeleted(id: string): boolean {
    return isRecentlyDeleted('TimeBox', id);
  }
}

// 導出單例
export const timeBoxRepository = new TimeBoxRepository();
